import React from 'react';

import { Grid, GridItem } from '@chakra-ui/react';
import {
  AvatarGroup,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MetricCard,
  MetricCardText,
  MetricCardTitle,
} from '@mesh.ai/rabbit';
import { capitalize } from 'lodash';

import noPendingReviews from 'assets/assetsV1/illustrations/deerIllustrations/noPendingReviews.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';

// Function to extract profile photo data
const getProfilePhotosData = (filteredData) =>
  filteredData?.map(({ employee }) => {
    const nameParts = employee?.displayName?.split(' ');
    // Extract the first letter of the first and last words
    const firstNameInitial = nameParts.length > 0 ? nameParts[0].charAt(0) : '';
    const lastNameInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : '';

    const altText = capitalize(firstNameInitial) + capitalize(lastNameInitial);

    return {
      fallbackText: altText,
      src: employee?.profilePhoto,
      tooltipText: employee?.displayName,
    };
  }) || [];

// Function to render metric cards
const renderMetricCard = (title, data, totalCount, notStartedCount, type) =>
  totalCount !== 0 &&
  notStartedCount !== 0 && (
    <GridItem colSpan={4}>
      <MetricCard>
        <MetricCardTitle>{title}</MetricCardTitle>
        {type !== 'SELF' && <AvatarGroup size="lg" data={data} />}

        <MetricCardText type="warning">
          {`${notStartedCount} of ${totalCount} reviews yet to be completed`}
        </MetricCardText>
      </MetricCard>
    </GridItem>
  );

// Array containing types and titles of review cards
const reviewCards = [
  { type: 'SELF', title: 'Self reviews' },
  { type: 'PEER', title: 'Peer reviews' },
  { type: 'UPWARD', title: 'Upward reviews' },
  { type: 'MANAGER', title: 'Manager reviews' },
  { type: 'DLM', title: 'Dotted line manager reviews' },
];

interface MetricsCardData {
  review: {
    providerType: string;
    submissionStatus: string;
  };

  employee: {
    profilePhoto: string;
  };
}

interface MetricsCardProps {
  cards?: MetricsCardData[];
  cardTitle: string;
}

export const ReviewsCards = ({ cards, cardTitle }: MetricsCardProps) => {
  // Function to get profile photo data for each review type
  const getProfilePhotoData = (providerType) => {
    const filteredData = cards?.filter(({ review }) => review.providerType === providerType) || [];
    const notStartedCount = filteredData.filter(
      ({ review }) => review.submissionStatus === 'NOT_STARTED' || review.submissionStatus === 'IN_PROGRESS'
    ).length;

    const totalCount = filteredData.length;

    return { totalCount, notStartedCount, mappedData: getProfilePhotosData(filteredData) };
  };

  //  hold the total notStartedCount of reviews
  let totalNotStartedCount = 0;

  // Calculate totalNotStartedCount and check if it's 0
  reviewCards.forEach(({ type }) => {
    const { notStartedCount } = getProfilePhotoData(type);
    totalNotStartedCount += notStartedCount;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{cardTitle}</CardTitle>
      </CardHeader>
      <CardContent
        style={{
          height: '346px',
          overflow: 'auto',
          padding: '1px',
        }}
      >
        {totalNotStartedCount === 0 ? (
          <EmptyScreen
            illustrationSrc={noPendingReviews}
            heading="No pending reviews!"
            isSmallTextSize
            description="You've either completed all pending reviews in the current cycle(s) or aren't part of any active cycles."
            illustrationStyle={{
              width: '180px',
              height: '160px',
            }}
          />
        ) : (
          <Grid gap={2}>
            {/* Mapping over review card types and rendering metric cards */}
            {reviewCards.map(({ type, title }) => {
              const { mappedData, totalCount, notStartedCount } = getProfilePhotoData(type);
              // Add notStartedCount to the total
              totalNotStartedCount += notStartedCount;
              return renderMetricCard(title, mappedData, totalCount, notStartedCount, type);
            })}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
